/* Birthdays (owner request 2026-11-01), a Little surprise: the person's own list, kept on this device and in the
   database (migration 017: each person reads and writes ONLY their own row; nobody else, not even the owner, can read it).
   - The device copy (localStorage, per person) is what the screen uses, so it works offline and opens at once; edits made
     here are marked "not sent yet" and sent when there is a connection (also after closing the app). Nothing is lost:
     before sending, this copy and the database's are merged (lib/birthdays.ts merge: each row by its latest change), and
     the database takes a change only over the version this device last saw (else: read, merge, try again). After
     sending, the list is read back and compared.
   - The Settings card (only while Little surprises is On): "Your birthday" (month, day, optional year) and
     "Family & friends" (name + month/day; add, edit, remove; up to 100). Names are shown as text, never as HTML.
   - Signed out: a copy with nothing waiting is forgotten; one with changes waiting stays, and is sent at that person's
     next sign-in. Sends nothing anywhere else, never touches a time entry. */
import {State} from './state.ts';
import * as db from './db.ts';
import {connected} from './syncer.ts';
import {escapeHtml} from './ui/common.ts';
import * as B from './lib/birthdays.ts';

interface Copy { data: B.Birthdays; rev: number | null; dirty: boolean }
const KEY = (id: string) => 'ot_bd_' + id;
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const put = (k: string, v: string | null) => { try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* private mode: kept in memory */ } };
const mem = new Map<string, Copy>();   // (private mode: the copy lives in memory only)
function read(id: string): Copy {
  const raw = get(KEY(id));
  if (raw !== null) {
    try { const v = JSON.parse(raw); return {data: B.parse(v.data), rev: Number.isInteger(v.rev) ? v.rev : null, dirty: v.dirty === true}; } catch { /* odd: start over */ }
  }
  return mem.get(id) || {data: B.empty(), rev: null, dirty: false};
}
let version = 0;
export const hooks = {changed: () => {}};
function write(id: string, c: Copy) {
  const before = read(id);
  mem.set(id, c); put(KEY(id), JSON.stringify({data: B.toJson(c.data), rev: c.rev, dirty: c.dirty}));
  if (!B.same(before.data, c.data)) { version++; hooks.changed(); }
}
/** the signed-in person's list as this device has it (null: nobody signed in) */
export const myList = (): B.Birthdays | null => State.meId ? read(State.meId).data : null;
/** goes up whenever the list changes (the header looks again) */
export const listVersion = () => version;
/** changes made on this device that have not reached the database yet */
export const waiting = () => !!State.meId && read(State.meId).dirty;

/* ---------- sending and fetching ---------- */
let running: Promise<void> | null = null, again = false, edits = 0, refusal = '', lastSync = 0;
let status: 'ok' | 'waiting' | 'refused' = 'ok';
/** make the device copy and the database's agree (only once the login is confirmed and the data is loaded) */
export function syncBirthdays(): Promise<void> {
  if (!State.meId || !connected() || typeof navigator !== 'undefined' && navigator.onLine === false) { status = waiting() ? 'waiting' : 'ok'; return Promise.resolve(); }
  if (running) { again = true; return running; }
  running = (async () => {
    const id = State.meId!;
    try {
      for (let tries = 0; tries < 5 && State.meId === id; tries++) {
        const seq = edits, local = read(id);
        const server = await db.myBirthdays(id);
        if (State.meId !== id) return;
        const sdata = server ? B.parse(server.data) : B.empty();
        if (!local.dirty) {   // nothing waiting here: the database's list is the list
          if (seq === edits) write(id, {data: sdata, rev: server ? server.rev : null, dirty: false});
          if (seq === edits) break; else continue;
        }
        const merged = B.merge(local.data, sdata);
        let rev = server ? server.rev : null;
        if (!server || !B.same(merged, sdata)) {
          rev = await db.saveBirthdays(id, B.toJson(merged), server ? server.rev : null);
          if (rev === null) continue;   // another device saved meanwhile: read, merge, again
          const back = await db.myBirthdays(id);   // read back: what the database now has
          if (!back || !B.same(B.parse(back.data), merged)) continue;
          rev = back.rev;
        }
        if (State.meId !== id) return;
        if (seq !== edits) { write(id, {data: B.merge(read(id).data, merged), rev, dirty: true}); continue; }   // edited meanwhile: send that too
        write(id, {data: merged, rev, dirty: false});
        refusal = ''; break;
      }
      status = waiting() ? 'waiting' : 'ok';
      lastSync = Date.now();
    } catch (e: any) {
      if (e instanceof db.Offline) status = waiting() ? 'waiting' : 'ok';
      else { status = 'refused'; refusal = String(e?.message || e); console.warn('birthdays not saved', refusal); }   // kept here, marked waiting
    } finally {
      running = null; paintStatus();
      if (again) { again = false; void syncBirthdays(); }
    }
  })();
  return running;
}
/** a catch-up or a live change (main.ts, syncer hooks.prefs): now = a change was announced; otherwise at most once a minute */
export function maybeSyncBirthdays(now: boolean) { if (now || waiting() || Date.now() - lastSync > 60_000) void syncBirthdays(); }
/** sign-out: a copy with nothing waiting is forgotten; one with changes waiting stays for this person's next sign-in */
export function forgetBirthdays(id: string | null) {
  if (!id) return;
  if (!read(id).dirty) { put(KEY(id), null); mem.delete(id); }
  status = 'ok'; refusal = '';
}
function edit(change: (b: B.Birthdays) => B.Birthdays) {
  const id = State.meId; if (!id) return;
  const c = read(id); edits++;
  write(id, {data: change(c.data), rev: c.rev, dirty: true});
  status = 'waiting'; void syncBirthdays();
}
if (typeof addEventListener === 'function') addEventListener('online', () => setTimeout(() => { if (waiting()) void syncBirthdays(); }, 1500));

/* ---------- the Settings card ---------- */
const opts = (n: number, sel: number | null, first: string, label = (i: number) => String(i)) =>
  `<option value="">${first}</option>` + Array.from({length: n}, (_, i) => `<option value="${i + 1}"${sel === i + 1 ? ' selected' : ''}>${label(i + 1)}</option>`).join('');
const monthOpts = (sel: number | null) => opts(12, sel, 'Month', i => B.MONTHS[i - 1]);
const dayOpts = (m: number | null, sel: number | null) => opts(m ? [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] : 31, sel, 'Day');
let editing = '', msg = '', ownMsg = '';
function statusText(): string {
  if (status === 'refused') return `Not saved to your account: ${refusal.replace(/^Birthdays: /, '')} It is kept on this device.`;
  if (waiting()) return 'Saved on this device; sent to your account when there is a connection.';
  return 'Saved to your account: it follows you to your other devices.';
}
function paintStatus() { const el = document.getElementById('bdStatus'); if (el) el.textContent = statusText(); }
function cardInner(): string {
  const b = myList() || B.empty(), own = b.own && b.own.m !== null ? b.own : null, people = B.live(b);
  const row = (p: B.Friend) => editing === p.id
    ? `<li class="bd-item editing" data-bd="${escapeHtml(p.id)}"><input id="bdEditName" maxlength="${B.NAME_MAX}" value="${escapeHtml(p.name)}" aria-label="Name" autocomplete="off">
        <div class="bd-md"><select id="bdEditM" aria-label="Month">${monthOpts(p.m)}</select><select id="bdEditD" aria-label="Day">${dayOpts(p.m, p.d)}</select></div>
        <div class="bd-acts"><button type="button" class="btn primary" id="bdEditSave">Save</button><button type="button" class="btn ghost" id="bdEditCancel">Cancel</button></div></li>`
    : `<li class="bd-item" data-bd="${escapeHtml(p.id)}"><span class="bd-name">${escapeHtml(p.name)}</span><span class="bd-date">${B.mdLabel(p.m, p.d)}</span>
        <button type="button" class="btn ghost bd-small" data-bd-edit="${escapeHtml(p.id)}" aria-label="Edit ${escapeHtml(p.name)}">Edit</button>
        <button type="button" class="btn ghost bd-small" data-bd-del="${escapeHtml(p.id)}" aria-label="Remove ${escapeHtml(p.name)}">Remove</button></li>`;
  const full = people.length >= B.MAX_PEOPLE;
  return `<h2>Birthdays</h2>
    <p class="muted-text">Only for you: nobody else can see this list, not even the owner. On the day, the app celebrates (only on your screen).</p>
    <div class="bd-sec"><div class="bd-lbl">Your birthday</div>
      <div class="bd-md"><select id="bdOwnM" aria-label="Your birthday: month">${monthOpts(own?.m ?? null)}</select><select id="bdOwnD" aria-label="Your birthday: day">${dayOpts(own?.m ?? null, own?.d ?? null)}</select>
        <input id="bdOwnY" inputmode="numeric" maxlength="4" placeholder="Year (optional)" aria-label="Your birthday: year (optional)" value="${own?.y ?? ''}" autocomplete="off"></div>
      <p class="muted-text bd-note">The year is optional. It is never shown to anyone, and no age is ever shown.${own ? ' <button type="button" class="bd-link" id="bdOwnClear">Clear</button>' : ''}</p>
      <p class="bd-msg" id="bdOwnMsg" role="status">${escapeHtml(ownMsg)}</p></div>
    <div class="bd-sec"><div class="bd-lbl">Family &amp; friends <span class="bd-count">${people.length ? `${people.length} of ${B.MAX_PEOPLE}` : ''}</span></div>
      <ul class="bd-list" id="bdList">${people.map(row).join('') || '<li class="bd-none muted-text">Nobody yet.</li>'}</ul>
      <div class="bd-add"><input id="bdName" maxlength="${B.NAME_MAX}" placeholder="Name, e.g. Mom" aria-label="Name" autocomplete="off"${full ? ' disabled' : ''}>
        <div class="bd-md"><select id="bdM" aria-label="Month"${full ? ' disabled' : ''}>${monthOpts(null)}</select><select id="bdD" aria-label="Day"${full ? ' disabled' : ''}>${dayOpts(null, null)}</select>
        <button type="button" class="btn primary" id="bdAdd"${full ? ' disabled' : ''}>Add</button></div></div>
      <p class="bd-msg" id="bdMsg" role="status">${escapeHtml(full ? `The list is full (${B.MAX_PEOPLE} people).` : msg)}</p></div>
    <p class="muted-text" id="bdStatus">${escapeHtml(statusText())}</p>`;
}
/** the card (surprises.ts puts it under the Little surprises switch, only while it is On) */
export const birthdaysCard = () => State.meId ? `<div class="card" id="birthdaysCard">${cardInner()}</div>` : '';
/** redraw only this card (never the rest of Settings: nothing else being typed is disturbed) */
function repaint(focus = '') {
  const card = document.getElementById('birthdaysCard'); if (!card) return;
  card.innerHTML = cardInner(); wireBirthdaysCard();
  if (focus) (document.getElementById(focus) as HTMLElement | null)?.focus();
}
const num = (el: HTMLInputElement | HTMLSelectElement | null) => el && el.value !== '' ? Number(el.value) : null;
/** a day list that fits the month (Feb: 29), keeping the chosen day when it still exists */
function fitDays(mSel: HTMLSelectElement | null, dSel: HTMLSelectElement | null) {
  if (!mSel || !dSel) return;
  const m = num(mSel), d = num(dSel);
  dSel.innerHTML = dayOpts(m, d !== null && m !== null && B.validMD(m, d) ? d : null);
}
export function wireBirthdaysCard() {
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;
  const oM = $<HTMLSelectElement>('bdOwnM'), oD = $<HTMLSelectElement>('bdOwnD'), oY = $<HTMLInputElement>('bdOwnY');
  const saveOwn = () => {
    const m = num(oM), d = num(oD), yRaw = (oY?.value || '').trim(), y = yRaw === '' ? null : Number(yRaw);
    const cur = myList()?.own, curSet = !!cur && cur.m !== null;
    if (m === null && d === null) { ownMsg = ''; if (curSet) { edit(b => B.setOwn(b, null, null, null)); repaint(); } return; }
    if (m === null || d === null || !B.validMD(m, d)) { ownMsg = 'Pick a month and a day.'; const el = $('bdOwnMsg'); if (el) el.textContent = ownMsg; return; }
    if (!B.validYear(y, m, d)) { ownMsg = m === 2 && d === 29 && y && y >= 1900 ? `February 29 did not exist in ${y}.` : `The year: 1900 to ${new Date().getFullYear()}, or leave it empty.`; const el = $('bdOwnMsg'); if (el) el.textContent = ownMsg; return; }
    ownMsg = '';
    if (curSet && cur!.m === m && cur!.d === d && cur!.y === y) { const el = $('bdOwnMsg'); if (el) el.textContent = ''; return; }
    edit(b => B.setOwn(b, m, d, y)); ownMsg = 'Saved.'; repaint(); ownMsg = '';
  };
  if (oM) oM.onchange = () => { fitDays(oM, oD); if (num(oD) !== null || num(oM) === null) saveOwn(); };
  if (oD) oD.onchange = saveOwn;
  if (oY) oY.onchange = saveOwn;
  const clear = $('bdOwnClear'); if (clear) clear.onclick = () => { edit(b => B.setOwn(b, null, null, null)); ownMsg = 'Cleared.'; repaint(); ownMsg = ''; };
  const aM = $<HTMLSelectElement>('bdM'), aD = $<HTMLSelectElement>('bdD'), aN = $<HTMLInputElement>('bdName');
  if (aM) aM.onchange = () => fitDays(aM, aD);
  const add = () => {
    const b = myList() || B.empty(), name = aN?.value || '', m = num(aM), d = num(aD);
    const why = B.friendProblem(b, name, m, d);
    if (why) { msg = why; const el = $('bdMsg'); if (el) el.textContent = why; return; }
    const shown = B.cleanName(name);
    edit(x => B.addFriend(x, crypto.randomUUID(), name, m!, d!)); msg = `Added ${shown} (${B.mdLabel(m!, d!)}).`; repaint('bdName'); msg = '';
  };
  const addBtn = $('bdAdd'); if (addBtn) addBtn.onclick = add;
  if (aN) aN.onkeydown = e => { if (e.key === 'Enter') { e.preventDefault(); add(); } };
  document.querySelectorAll<HTMLButtonElement>('#birthdaysCard [data-bd-edit]').forEach(btn => btn.onclick = () => { editing = btn.dataset.bdEdit!; msg = ''; repaint('bdEditName'); });
  document.querySelectorAll<HTMLButtonElement>('#birthdaysCard [data-bd-del]').forEach(btn => btn.onclick = () => {
    const p = B.live(myList() || B.empty()).find(x => x.id === btn.dataset.bdDel); if (!p) return;
    if (!confirm(`Remove ${p.name}'s birthday?`)) return;
    edit(b => B.removeFriend(b, p.id)); if (editing === p.id) editing = ''; msg = `Removed ${p.name}.`; repaint(); msg = '';
  });
  const eM = $<HTMLSelectElement>('bdEditM'), eD = $<HTMLSelectElement>('bdEditD'), eN = $<HTMLInputElement>('bdEditName');
  if (eM) eM.onchange = () => fitDays(eM, eD);
  const saveEdit = () => {
    const b = myList() || B.empty(), id = editing, name = eN?.value || '', m = num(eM), d = num(eD);
    const why = B.friendProblem(b, name, m, d, id);
    if (why) { msg = why; const el = $('bdMsg'); if (el) el.textContent = why; return; }
    edit(x => B.editFriend(x, id, name, m!, d!)); editing = ''; msg = 'Saved.'; repaint(); msg = '';
  };
  const es = $('bdEditSave'); if (es) es.onclick = saveEdit;
  if (eN) eN.onkeydown = e => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } else if (e.key === 'Escape') { editing = ''; repaint(); } };
  const ec = $('bdEditCancel'); if (ec) ec.onclick = () => { editing = ''; msg = ''; repaint(); };
}
/** the list changed (another device, a merge): redraw the card if it is open and nobody is typing in it */
export function refreshCard() {
  const card = document.getElementById('birthdaysCard'), a = document.activeElement;
  if (!card || (a && card.contains(a) && /^(INPUT|SELECT)$/.test(a.tagName)) || editing) { paintStatus(); return; }
  repaint();
}
